import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { BgpCommunities } from '@ipfabric/utils/settings/types/types';
import { createBgpCsvConfig } from 'components/BgpSettings/dataTransfer/bgpCsvConfig';
import { bgpFileHeaders } from 'components/BgpSettings/dataTransfer/bgpDataTransferTypes';
import { DataTransferForm } from 'components/DataTransferForm/DataTransferForm';
import { exportDataToCsv } from 'components/DataTransferForm/helpers/csvExport';
import type { FC } from 'react';
import { generateGuid } from 'utils/guid';

type Props = {
  existingBgpEntries: BgpCommunities[];
  onImportSuccess: (data: BgpCommunities[], replaceEntries: boolean) => void;
  onClose: () => void;
};

export const BgpDataTransferForm: FC<Props> = ({
  existingBgpEntries,
  onImportSuccess,
  onClose,
}) => {
  const csvConfig = createBgpCsvConfig();

  const mapBgpItemToCsvRow = (item: Record<string, unknown>) => {
    const sn = typeof item.sn === 'string' ? item.sn : '';
    const protocol = typeof item.protocol === 'string' ? item.protocol : '';
    const vrf = typeof item.vrf === 'string' ? item.vrf : null;
    const isVrfStd = typeof item.isVrfStd === 'boolean' ? item.isVrfStd : false;
    const communityList = Array.isArray(item.communityList)
      ? item.communityList
      : [];

    return [
      sn,
      protocol,
      vrf,
      isVrfStd ? 'yes' : 'no',
      communityList.join(','),
    ];
  };

  const handleExport = useStableCallback(() => {
    exportDataToCsv(
      'BGP_Communities',
      bgpFileHeaders,
      existingBgpEntries,
      mapBgpItemToCsvRow,
    );
  });

  const handleDownloadExample = useStableCallback(() => {
    const exampleEntries: BgpCommunities[] = [
      {
        id: generateGuid(),
        sn: 'a23ff71',
        protocol: 'IPv4',
        vrf: null,
        isVrfStd: true,
        communityList: ['100:10', '100:20', '100:30'],
      },
      {
        id: generateGuid(),
        sn: 'a23ff71',
        protocol: 'IPv6',
        vrf: 'customerA',
        isVrfStd: false,
        communityList: ['101:10', '101:20', '101:30'],
      },
      {
        id: generateGuid(),
        sn: 'a27ff15',
        protocol: 'IPv4',
        vrf: null,
        isVrfStd: true,
        communityList: ['200:10', '200:20', '200:30'],
      },
    ];

    exportDataToCsv(
      'BGP_Example',
      bgpFileHeaders,
      exampleEntries,
      mapBgpItemToCsvRow,
    );
  });

  return (
    <DataTransferForm
      title='Import / Export data'
      existingData={existingBgpEntries}
      exportDescription='Exports entire table (including full lists of communities) as a CSV file.'
      exportButtonText='Export data'
      importDescription='You can add more devices to this table (or replace its data completely) using a CSV file. Download an example file to see how it should be structured.'
      importButtonText='Import data'
      importReplaceButtonText='Import & replace'
      downloadExampleText='Download example file'
      fileFieldName='bgpFileUpload'
      csvConfig={csvConfig}
      onExport={handleExport}
      onDownloadExample={handleDownloadExample}
      onImportSuccess={onImportSuccess}
      onClose={onClose}
    />
  );
};
